import { c as _c } from "react/compiler-runtime";
import { IcoBook16, IcoConstruct16, IcoDatabase16, IcoDragAndDrop16, IcoFileText16, IcoKey16, IcoPlusSmall16, IcoScroll16, IcoServer16 } from '@onefootprint/icons';
import { IconButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { type FocusEvent, type KeyboardEvent, type PointerEvent, type ReactNode, type Ref, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePublishContextSelection } from '@/components/agent-chat';
import { type Row, useNavModel } from '../../hooks/use-nav-model';
import useNavReorder from '../../hooks/use-nav-reorder';
import { keysProducedAbove } from '../../store/catalog-position';
import { inputsKeyError } from '../../store/field-key';
import { useBuilder } from '../../store/provider';
import { hasUnsavedInputs, hasUnsavedTools } from '../../store/reducer';
import { useNormsSuggestions, useSkillsSuggestions } from '../../store/suggestions';
import { INPUT_NODE_ID, OUTPUTS_NODE_ID, type ViewMode } from '../../store/types';
import TaskTemplatePicker, { type CatalogPick } from './task-template-picker';
type NavProps = {
  open: boolean;
  header?: ReactNode;
  viewMode?: ViewMode;
  onViewModeChange?: (view: ViewMode) => void;
};
const Nav = ({
  open,
  header,
  viewMode = 'plan',
  onViewModeChange
}: NavProps) => {
  const {
    t
  } = useTranslation('agents', {
    keyPrefix: 'agentBuilder.nav'
  });
  const reorderHintId = useId();
  const {
    planEntries,
    isActive,
    jumpTo,
    currentId
  } = useNavModel(viewMode === 'plan');
  const {
    actions,
    state,
    isReadOnly,
    isSavingGraph
  } = useBuilder();
  // Served by the builder response so it stays in step with the server's own permission check.
  const showDecision = state.canAddDecisionNodes;
  const [pickerOpen, setPickerOpen] = useState(false);
  const normsSuggestionCount = useNormsSuggestions().length;
  const skillsSuggestionCount = useSkillsSuggestions().length;
  const currentRow = planEntries.find(entry => entry.row.id === currentId)?.row;
  const sectionNames = {
    tools: 'Tools',
    mcp: 'MCP',
    norms: 'Norms',
    skills: 'Skills',
    credentials: 'Credentials'
  };
  const publishSelection = usePublishContextSelection(viewMode === 'plan' ? currentRow ? {
    id: currentRow.contextNodeId ?? currentRow.id,
    name: currentRow.label
  } : null : {
    id: viewMode,
    name: sectionNames[viewMode]
  });
  const canReorder = !isReadOnly && !isSavingGraph;
  const moveTask = (from: number, to: number) => {
    const node = state.bundle.nodes[from];
    if (!node) return;
    const direction = to > from ? 'down' : 'up';
    for (let step = 0; step < Math.abs(to - from); step++) actions.moveNode(node.id, direction);
  };
  const reorder = useNavReorder({
    disabled: !canReorder || !open,
    itemIds: state.bundle.nodes.map(node_0 => node_0.id),
    onReorder: moveTask
  });
  const tasks = planEntries.filter(entry_0 => entry_0.row.id !== INPUT_NODE_ID && entry_0.row.id !== OUTPUTS_NODE_ID);
  const taskOrder = tasks.map(entry_1 => entry_1.row.id).join(',');
  const taskButtons = useRef(new Map<string, HTMLButtonElement>());
  const [movedByKey, setMovedByKey] = useState<{
    id: string;
    order: string;
  } | null>(null);
  // React reorders a list by moving the rows that fall behind, so a row moved down is detached and
  // reinserted, and the browser drops its focus to the body.
  useEffect(() => {
    if (!movedByKey || movedByKey.order === taskOrder) return;
    setMovedByKey(null);
    const button = taskButtons.current.get(movedByKey.id);
    if (!button) return;
    if (button.ownerDocument.activeElement === button.ownerDocument.body) button.focus();
  }, [movedByKey, taskOrder]);
  // Dragging needs a pointer, so the same move is on the keyboard for a focused row.
  const moveTaskByKey = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (!canReorder || !event.altKey || event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    const to_0 = event.key === 'ArrowUp' ? index - 1 : index + 1;
    if (to_0 < 0 || to_0 >= tasks.length) return;
    setMovedByKey({
      id: tasks[index].row.id,
      order: taskOrder
    });
    moveTask(index, to_0);
  };
  const navigate = (id: string) => {
    const row = planEntries.find(entry_2 => entry_2.row.id === id)?.row;
    if (row) publishSelection({
      id: row.contextNodeId ?? row.id,
      name: row.label
    });
    onViewModeChange?.('plan');
    jumpTo(id);
  };
  const addTask = () => {
    onViewModeChange?.('plan');
    actions.addTask();
  };
  const addRules = () => {
    onViewModeChange?.('plan');
    actions.addRulesNode();
  };
  const addDecision = () => {
    onViewModeChange?.('plan');
    actions.addDecisionNode();
  };
  const addCatalogTask = (pick: CatalogPick) => {
    onViewModeChange?.('plan');
    actions.addCatalogTask(pick);
  };
  return <aside aria-label="Plan navigation" inert={!open} className={cx('flex flex-col shrink-0 overflow-hidden bg-primary transition-[width] duration-200 ease-in-out max-md:absolute max-md:inset-y-0 max-md:left-0 max-md:z-30 max-md:max-w-[85%]', open ? 'w-[260px] border-r border-tertiary' : 'w-0')}>
      {header}
      <nav aria-label="Agent configuration" className="w-[260px] min-h-0 flex-1 overflow-y-auto px-3 py-4">
        <div className="flex flex-col gap-0.5">
          <NavRow row={{
          id: 'tools',
          label: 'Tools'
        }} marker={<IcoConstruct16 />} active={viewMode === 'tools'} onClick={() => onViewModeChange?.('tools')} />
          <NavRow row={{
          id: 'mcp',
          label: 'MCP'
        }} marker={<IcoServer16 />} active={viewMode === 'mcp'} onClick={() => onViewModeChange?.('mcp')} />
          <NavRow row={{
          id: 'norms',
          label: 'Norms'
        }} marker={<IcoScroll16 />} count={normsSuggestionCount} active={viewMode === 'norms'} onClick={() => onViewModeChange?.('norms')} />
          <NavRow row={{
          id: 'skills',
          label: 'Skills'
        }} marker={<IcoBook16 />} count={skillsSuggestionCount} active={viewMode === 'skills'} onClick={() => onViewModeChange?.('skills')} />
          <NavRow row={{
          id: 'credentials',
          label: 'Credentials'
        }} marker={<IcoKey16 />} active={viewMode === 'credentials'} onClick={() => onViewModeChange?.('credentials')} />
        </div>
        <div className="mt-5">
          <NavRow row={{
          id: INPUT_NODE_ID,
          label: 'Input schema'
        }} marker={<IcoDatabase16 />} active={viewMode === 'plan' && isActive(INPUT_NODE_ID)} onClick={() => navigate(INPUT_NODE_ID)} />
        </div>
        <div className="mt-5 flex h-7 items-center justify-between gap-2 pl-1.5">
          <span className="text-label-3 text-tertiary">Tasks</span>
          {!isReadOnly && <IconButton aria-label="Add task" icon={IcoPlusSmall16} size="compact" variant="ghost" disabled={isSavingGraph} onClick={() => setPickerOpen(true)} />}
        </div>
        <div ref={reorder.listRef} onClickCapture={reorder.onClickCapture} className="mt-1 flex flex-col gap-0.5">
          {tasks.map((entry_3, index_0) => <div key={entry_3.row.id} data-nav-task className="relative">
              {reorder.dropIndex === index_0 && <div className="pointer-events-none absolute inset-x-0 -top-px h-px bg-accent" />}
              <NavRow buttonRef={button_0 => {
            if (button_0) taskButtons.current.set(entry_3.row.id, button_0);else taskButtons.current.delete(entry_3.row.id);
          }} onPointerDown={event_0 => reorder.startDrag(event_0, index_0)} onKeyDown={event_1 => moveTaskByKey(event_1, index_0)} onBlur={event_2 => {
            if (event_2.relatedTarget) setMovedByKey(null);
          }} describedBy={canReorder ? reorderHintId : undefined} draggable={canReorder} dragging={reorder.dragIndex === index_0} row={entry_3.row} marker={index_0 + 1} active={viewMode === 'plan' && isActive(entry_3.row.id)} onClick={() => navigate(entry_3.row.id)} />
              {index_0 === tasks.length - 1 && reorder.dropIndex === tasks.length && <div className="pointer-events-none absolute inset-x-0 -bottom-px h-px bg-accent" />}
            </div>)}
        </div>
        <span hidden id={reorderHintId}>
          {t($ => $.reorderHint)}
        </span>
        <div className="mt-5">
          <NavRow row={{
          id: OUTPUTS_NODE_ID,
          label: 'Output'
        }} marker={<IcoFileText16 />} active={viewMode === 'plan' && isActive(OUTPUTS_NODE_ID)} onClick={() => navigate(OUTPUTS_NODE_ID)} />
        </div>
      </nav>
      <TaskTemplatePicker onPaste={() => {
      onViewModeChange?.('plan');
      actions.pasteNode({});
    }} open={pickerOpen} onClose={() => setPickerOpen(false)} onPickTask={addTask} onPickRules={addRules} onPickDecision={addDecision} showDecision={showDecision} onPickCatalog={addCatalogTask} inputs={state.bundle.inputs} producedKeys={keysProducedAbove(state.bundle.nodes)} tools={state.bundle.tools} inputsLocked={!!inputsKeyError(state.bundle.inputs) || hasUnsavedInputs(state)} toolsLocked={hasUnsavedTools(state)} disabled={isSavingGraph || isReadOnly} />
    </aside>;
};
type NavRowProps = {
  row: Row;
  active: boolean;
  onClick: () => void;
  marker: ReactNode;
  count?: number;
  onPointerDown?: (event: PointerEvent<HTMLButtonElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  onBlur?: (event: FocusEvent<HTMLButtonElement>) => void;
  buttonRef?: Ref<HTMLButtonElement>;
  describedBy?: string;
  draggable?: boolean;
  dragging?: boolean;
};
const NavRow = t0 => {
  const $ = _c(42);
  const {
    row,
    active,
    onClick,
    marker,
    count,
    onPointerDown,
    onKeyDown,
    onBlur,
    buttonRef,
    describedBy,
    draggable,
    dragging
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.nav"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const t2 = count ? `${row.label}, ${count} ${count === 1 ? "suggestion" : "suggestions"}` : undefined;
  const t3 = active ? "location" : undefined;
  let t4;
  if ($[1] !== draggable || $[2] !== t) {
    t4 = draggable ? t(_temp) : undefined;
    $[1] = draggable;
    $[2] = t;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = draggable && "touch-none select-none cursor-grab active:cursor-grabbing";
  const t6 = dragging && "opacity-40";
  const t7 = active ? "text-primary bg-senary/70" : "text-tertiary hover:text-primary hover:bg-hover";
  let t8;
  if ($[4] !== t5 || $[5] !== t6 || $[6] !== t7) {
    t8 = cx("group/navrow w-full min-w-0 h-7 max-md:h-9 px-1.5 flex items-center gap-2 text-left rounded-default cursor-pointer outline-none transition-colors focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-inset", t5, t6, t7);
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = draggable && "group-hover/navrow:hidden";
  let t10;
  if ($[8] !== t9) {
    t10 = cx(t9);
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== marker || $[11] !== t10) {
    t11 = <span className={t10}>{marker}</span>;
    $[10] = marker;
    $[11] = t10;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== draggable) {
    t12 = draggable && <IcoDragAndDrop16 color="tertiary" className="hidden group-hover/navrow:block" />;
    $[13] = draggable;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== t11 || $[16] !== t12) {
    t13 = <span aria-hidden={true} className="flex w-4 shrink-0 items-center justify-center text-caption-3 tabular-nums">{t11}{t12}</span>;
    $[15] = t11;
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const t14 = active ? "text-label-3" : "text-body-3";
  let t15;
  if ($[18] !== t14) {
    t15 = cx("min-w-0 flex-1 truncate", t14);
    $[18] = t14;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== row.label || $[21] !== t15) {
    t16 = <span className={t15}>{row.label}</span>;
    $[20] = row.label;
    $[21] = t15;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== row.badge) {
    t17 = row.badge && <span className="shrink-0 text-snippet-3 px-1 py-0.5 bg-secondary text-tertiary border border-tertiary rounded-sm">{row.badge}</span>;
    $[23] = row.badge;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== count) {
    t18 = !!count && <span className="shrink-0 min-w-4 h-4 px-1 rounded-full bg-accent/10 text-accent text-snippet-3 flex items-center justify-center">{count}</span>;
    $[25] = count;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== buttonRef || $[28] !== describedBy || $[29] !== onBlur || $[30] !== onClick || $[31] !== onKeyDown || $[32] !== onPointerDown || $[33] !== t13 || $[34] !== t16 || $[35] !== t17 || $[36] !== t18 || $[37] !== t2 || $[38] !== t3 || $[39] !== t4 || $[40] !== t8) {
    t19 = <button ref={buttonRef} type="button" onClick={onClick} onPointerDown={onPointerDown} onKeyDown={onKeyDown} onBlur={onBlur} aria-describedby={describedBy} aria-label={t2} aria-current={t3} title={t4} className={t8}>{t13}{t16}{t17}{t18}</button>;
    $[27] = buttonRef;
    $[28] = describedBy;
    $[29] = onBlur;
    $[30] = onClick;
    $[31] = onKeyDown;
    $[32] = onPointerDown;
    $[33] = t13;
    $[34] = t16;
    $[35] = t17;
    $[36] = t18;
    $[37] = t2;
    $[38] = t3;
    $[39] = t4;
    $[40] = t8;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  return t19;
};
export default Nav;
function _temp($) {
  return $.dragHint;
}